/**
 * Words of the review journey (F23): stage labels, states as text, and the
 * small helpers that turn counts and source lists into plain sentences.
 * British English, verb-first actions, and no AI provider or model name
 * anywhere (CONT-058).
 *
 * Implements JRNY-020 (labels) and JRNY-031 (source names).
 */
import type { ApiSearchSource } from '@bioterafi/api-contract';
import { formatInteger } from '../../format/format';
import { SOURCE_LABEL } from '../search/searchLabels';

export type StageKey = 'question' | 'protocol' | 'search' | 'run' | 'duplicates' | 'screening' | 'extraction' | 'results';

export type StageState = 'done' | 'needs_you' | 'ready' | 'running' | 'waiting' | 'coming_soon';

/** JRNY-020: the eight stages in process order. */
export const STAGE_ORDER: readonly StageKey[] = ['question', 'protocol', 'search', 'run', 'duplicates', 'screening', 'extraction', 'results'];

export const STAGE_LABEL: Readonly<Record<StageKey, string>> = {
  question: 'Question',
  protocol: 'Protocol',
  search: 'Search strategies',
  run: 'Run',
  duplicates: 'Duplicates',
  screening: 'Screening',
  extraction: 'Extraction and risk of bias',
  results: 'Results and certainty',
};

/** A state in words, lower case, for accessible names ("Search, needs you, 1"). */
export const STATE_WORDS: Readonly<Record<StageState, string>> = {
  done: 'done',
  needs_you: 'needs you',
  ready: 'ready to start',
  running: 'running',
  waiting: 'waiting',
  coming_soon: 'coming soon',
};

/** A state as visible text, sentence case, with the count when a person is needed. */
export function stateText(state: StageState, pending: number | null): string {
  if (state === 'needs_you') return pending ? `Needs you: ${formatInteger(pending)}` : 'Needs you';
  const words = STATE_WORDS[state];
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The state for an accessible name: "needs you, 1", "done". */
export function stateWordsWithCount(state: StageState, pending: number | null): string {
  return state === 'needs_you' && pending ? `${STATE_WORDS[state]}, ${formatInteger(pending)}` : STATE_WORDS[state];
}

export function sourceName(source: ApiSearchSource): string {
  return SOURCE_LABEL[source];
}

/** "PubMed", "PubMed and Europe PMC", "PubMed, Europe PMC and OpenAlex". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "1 record", "1,193 records". */
export function count(n: number, one: string, many = `${one}s`): string {
  return `${formatInteger(n)} ${n === 1 ? one : many}`;
}

/** Upper-cases the first letter of a sentence built from parts. */
export function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
}

/** JRNY-042 as amended 13 October 2026: risk of bias is built (F17), so no stage is labelled as coming soon for it. */
export const NO_ROB_FACTS = { assessments: 0, awaitingSignOff: 0, signedOff: 0, failed: 0 } as const;
export const RESULTS_WAITING = 'Results appear here once included studies have confirmed data.';
/** JRNY-040, acceptance criterion 2. */
export const NOTHING_NEEDS_YOU = 'Nothing needs you right now.';
/** JRNY-061. */
export const FROZEN_ANNOUNCEMENT = 'Protocol frozen. Your review is set up. Next, approve the search strategies.';
